import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import MobileMenu from './MobileMenu';

interface HeaderProps {
  currentPage?: string;
  phoneNumber?: string;
  companyName?: string;
}

const services = [
  { name: "Broken Pipe Repair", href: "/services/broken-pipe" },
  { name: "Leaky Plumbing Repair", href: "/services/leaky-plumbing" },
  { name: "Water Heater Replacement", href: "/services/water-heater-replacement" },
  { name: "Reverse Osmosis Installation", href: "/services/reverse-osmosis" },
  { name: "Toilet Installation", href: "/services/toilet-installation" },
  { name: "Emergency Water Cleanup", href: "/services/water-cleanup" },
  { name: "Emergency Plumbing Service", href: "/services/emergency-plumbing-service" }
];

const Header: React.FC<HeaderProps> = ({ 
  currentPage = '', 
  phoneNumber = "(630) 715-8829",
  companyName = "Partners Plumbing Co."
}) => {
  const isActive = (path: string) => currentPage === path;

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Header */}
        <div className="hidden lg:flex justify-between items-center py-3 md:py-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link href="/" className="flex items-center hover:opacity-80 transition-opacity">
              <Image
                src="/images/optimized/logo-desktop.webp"
                alt={companyName}
                width={280}
                height={67}
                className="h-12 md:h-16 w-auto"
                priority
                sizes="(max-width: 768px) 200px, 280px"
                placeholder="blur"
                blurDataURL="data:image/webp;base64,UklGRnoAAABXRUJQVlA4WAoAAAAQAAAADwAABwAAQUxQSDIAAAARL0AmbZurmr57yyIiqE8oiG0bejIYEQTgqiDA9vqnsUSI6H+oAERp2HZ65qP/VIAWAFZQOCBCAAAA8AEAnQEqEAAIAAVAfCWkAALp8sF8rgRgAP7o9FDvMCkMde9PK7euH5M1m6VWoDXf2FkP3BqV0ZYbO6NA/VFIAAAA"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="flex space-x-8">
            <Link 
              href="/" 
              className={`transition-colors ${
                isActive('/') 
                  ? 'text-slate-800 font-semibold' 
                  : 'text-gray-700 hover:text-slate-900 font-medium'
              }`}
            >
              Home
            </Link>
            
            {/* Services Dropdown */}
            <div className="relative group">
              <button className={`transition-colors flex items-center ${
                currentPage.startsWith('/services') 
                  ? 'text-slate-800 font-semibold' 
                  : 'text-gray-700 hover:text-slate-900 font-medium'
              }`}>
                Services
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <Link 
                  href="/services" 
                  className={`block px-4 py-2 border-b border-gray-100 ${
                    isActive('/services') 
                      ? 'text-slate-800 bg-slate-50 font-semibold' 
                      : 'text-gray-700 hover:bg-slate-50 hover:text-slate-900 font-medium'
                  }`}
                >
                  All Services
                </Link>
                {services.map((service, index) => (
                  <Link
                    key={index}
                    href={service.href}
                    className={`block px-4 py-2 transition-colors ${
                      isActive(service.href) 
                        ? 'text-slate-800 bg-slate-50 font-semibold' 
                        : 'text-gray-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            </div>
            
            <Link 
              href="/about" 
              className={`transition-colors ${
                isActive('/about') 
                  ? 'text-slate-800 font-semibold' 
                  : 'text-gray-700 hover:text-slate-900 font-medium'
              }`}
            >
              About
            </Link>
            <Link 
              href="/blog" 
              className={`transition-colors ${
                isActive('/blog') 
                  ? 'text-slate-800 font-semibold' 
                  : 'text-gray-700 hover:text-slate-900 font-medium'
              }`}
            >
              Blog
            </Link>
            <Link 
              href="/contact" 
              className={`transition-colors ${
                isActive('/contact') 
                  ? 'text-slate-800 font-semibold' 
                  : 'text-gray-700 hover:text-slate-900 font-medium'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Desktop CTA */}
          <div className="flex items-center space-x-4">
            <a href={`tel:${phoneNumber}`} className="text-lg xl:text-xl font-bold text-slate-800 hover:text-slate-900 transition-colors">
              {phoneNumber}
            </a>
            <Link href="/contact" className="bg-slate-800 text-white px-4 xl:px-6 py-2 rounded-lg font-semibold hover:bg-slate-900 transition-all duration-200 shadow-md hover:shadow-lg text-sm xl:text-base">
              Schedule Service
            </Link>
          </div>
        </div>

        {/* Mobile Header */}
        <MobileMenu 
          currentPage={currentPage}
          phoneNumber={phoneNumber}
          companyName={companyName}
          services={services}
          isActive={isActive}
        />
      </div>
    </header>
  );
};

export default Header; 